import type { TreeNode } from '../schemas/tree-node'
import { isRangeValue, isRollingDateValue } from '../schemas/filter-entity'
import type { FilterEntityFields, Operator, RightValue } from '../schemas/filter-entity'
import { getExchangeToday } from '@/lib/exchange-time'
import type { TreeState } from '../reducer/tree-reducer'
import type { QueryFilterGroup, QueryFilterTree } from '@/services/api/scanner-types'

// ─── Types ───

export interface StickyColumnConfig {
  column: string
  defaultOperator: Operator
  defaultValue: RightValue
  /**
   * Date columns only: the rolling keys (`ROLLING_DATE_OPTIONS`) this scope may take, in
   * display order. When set, the pinned row offers just these instead of the full calendar
   * picker, so the window always ends today (Ticker Stats: a static range would stop
   * including new days).
   */
  rollingOptions?: readonly string[]
}

// ─── Helpers ───

/** Deterministic ID for a sticky column — avoids collisions with genId's `n_` prefix. */
function stickyId(column: string): string {
  return `sticky_${column}`
}

/**
 * The operator and value a sticky row should carry, given what the tree or a saved profile had.
 *
 * A rolling-only scope (`rollingOptions`) is always `defaultOperator` over a window through
 * today. Anything else left behind is coerced back, or the backend gets a comparison against a
 * two-date list and 500s (prod, 2026-09-27: `day0_date >= ['2026-03-27', '2026-09-27']`):
 * a window keeps its rolling low bound, a lone rolling key becomes the low bound, and anything
 * else (a fixed date, a key the scope does not offer) falls back to the default window.
 */
export function normalizeStickyEntry(
  config: StickyColumnConfig,
  existing?: { operator: Operator; value: RightValue },
): { operator: Operator; value: RightValue } {
  if (!config.rollingOptions) {
    return {
      operator: existing?.operator ?? config.defaultOperator,
      value: existing?.value ?? config.defaultValue,
    }
  }
  const value = existing?.value
  if (existing?.operator === config.defaultOperator && isRangeValue(value)) {
    if (isRollingDateValue(value.low) && config.rollingOptions.includes(value.low)) return existing
  }
  const low = isRangeValue(value) ? value.low : value
  if (isRollingDateValue(low) && config.rollingOptions.includes(low)) {
    return { operator: config.defaultOperator, value: { low, high: getExchangeToday() } }
  }
  return { operator: config.defaultOperator, value: config.defaultValue }
}

/**
 * Extract the current operator and value for sticky columns from existing tree state.
 * Returns a map of column → { operator, value }.
 */
export function extractStickyValues(
  state: TreeState,
  stickyColumns: Set<string>,
): Map<string, { operator: Operator; value: RightValue }> {
  const result = new Map<string, { operator: Operator; value: RightValue }>()

  for (const [, entity] of Object.entries(state.entities)) {
    if (stickyColumns.has(entity.left.column)) {
      result.set(entity.left.column, {
        operator: entity.operator,
        value: entity.right,
      })
    }
  }

  return result
}

/**
 * Ensure sticky filters are present in a tree state.
 *
 * 1. Removes any existing nodes that match sticky column keys
 * 2. Prepends sticky filter nodes at the beginning of the root group
 *
 * Used when loading profiles to preserve sticky filters.
 */
export function ensureStickyFilters(
  state: {
    nodes: Record<string, TreeNode>
    entities: Record<string, FilterEntityFields>
    rootId: string
  },
  stickyConfigs: StickyColumnConfig[],
  currentValues?: Map<string, { operator: Operator; value: RightValue }>,
): void {
  if (stickyConfigs.length === 0) return

  const stickyColumnSet = new Set(stickyConfigs.map((c) => c.column))

  // Find the root's first child group (the main AND/OR group)
  const rootNode = state.nodes[state.rootId]
  if (!rootNode || !('children' in rootNode) || rootNode.children.length === 0) return

  const mainGroupId = rootNode.children[0]
  const mainGroup = state.nodes[mainGroupId]
  if (!mainGroup || !('children' in mainGroup)) return

  // Remove any existing sticky column nodes from the tree
  const toRemove: string[] = []
  for (const childId of mainGroup.children) {
    const entity = state.entities[childId]
    if (entity && stickyColumnSet.has(entity.left.column)) {
      toRemove.push(childId)
    }
  }
  for (const id of toRemove) {
    const idx = mainGroup.children.indexOf(id)
    if (idx !== -1) mainGroup.children.splice(idx, 1)
    delete state.nodes[id]
    delete state.entities[id]
  }

  // Prepend sticky filters at the beginning
  const stickyIds: string[] = []
  for (const config of stickyConfigs) {
    const id = stickyId(config.column)
    const { operator, value } = normalizeStickyEntry(config, currentValues?.get(config.column))

    state.nodes[id] = { id, type: 'FILTER' as const, parentId: mainGroupId }
    state.entities[id] = {
      left: { column: config.column },
      operator,
      right: value,
      rightType: 'value',
      enabled: true,
    }
    stickyIds.push(id)
  }

  mainGroup.children = [...stickyIds, ...mainGroup.children]
}

/**
 * Build a set of sticky column keys from a config array.
 */
export function buildStickyColumnSet(configs: StickyColumnConfig[]): Set<string> {
  return new Set(configs.map((c) => c.column))
}

/** True when a query-tree node is a leaf on one of the given columns. */
function isStickyLeaf(node: QueryFilterTree, stickyColumns: ReadonlySet<string>): boolean {
  const column = (node as { left?: { column?: string } }).left?.column
  return column != null && stickyColumns.has(column)
}

/**
 * Remove sticky-column leaves from a query-shaped filter group. Sticky rows
 * are page scope, not profile content — the (modified) comparison and any
 * profile snapshot must exclude them or every loaded profile immediately
 * reads as edited. Top-level only, same invariant as extraction above.
 */
export function stripStickyFromQuery(
  query: QueryFilterGroup,
  stickyColumns: ReadonlySet<string>,
): QueryFilterGroup {
  if (stickyColumns.size === 0) return query
  const key = Array.isArray(query.AND) ? 'AND' : Array.isArray(query.OR) ? 'OR' : null
  if (!key) return query
  const children = (query[key] as QueryFilterTree[]).filter(
    (node) => !isStickyLeaf(node, stickyColumns),
  )
  return { ...query, [key]: children }
}

/**
 * Remove sticky-column leaves from PROFILE-shaped filters
 * (`[{AND: [{column, operator, value}]}]`, the `formToProfile` output).
 * Applied when snapshotting the form for Save / Save As, so stored profiles
 * never embed the page's transient date scope.
 */
export function stripStickyFromProfile(
  profile: Array<Record<string, unknown>>,
  stickyColumns: ReadonlySet<string>,
): Array<Record<string, unknown>> {
  if (stickyColumns.size === 0) return profile
  return profile.map((group) => {
    const key = Array.isArray(group.AND) ? 'AND' : Array.isArray(group.OR) ? 'OR' : null
    if (!key) return group
    const children = (group[key] as unknown[]).filter((leaf) => {
      const column = (leaf as { column?: string } | null)?.column
      return column == null || !stickyColumns.has(column)
    })
    return { ...group, [key]: children }
  })
}
